import React from 'react';
import { Users } from 'lucide-react';

export const JohariWindow: React.FC = () => {
  const quadrants = [
    {
      id: 'open',
      title: 'Open Self',
      description: 'Known to self and others',
      color: 'primary',
      examples: ['Your name', 'Your job title', 'Your public skills', 'Your visible behaviors']
    },
    {
      id: 'blind',
      title: 'Blind Spot',
      description: 'Known to others, not to self',
      color: 'secondary',
      examples: ['Your communication style', 'Your body language', 'Your impact on others', 'Your unconscious habits']
    },
    {
      id: 'hidden',
      title: 'Hidden Self',
      description: 'Known to self, not to others',
      color: 'accent',
      examples: ['Your private thoughts', 'Your fears', 'Your personal goals', 'Your hidden talents']
    },
    {
      id: 'unknown',
      title: 'Unknown Self',
      description: 'Unknown to self and others',
      color: 'coral',
      examples: ['Your untapped potential', 'Your unconscious patterns', 'Your future capabilities', 'Your hidden strengths']
    }
  ];

  const getColorClasses = (_color: string) => {
    return 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-pri)]';
  };

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="h-12 w-12 text-consulting-slate-blue-600" />
            <h1 className="text-4xl font-bold text-charcoal-900">Johari Window</h1>
          </div>
          <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
            A framework for understanding self-awareness and self-disclosure, 
            helping you see what you know about yourself and what others know about you.
          </p>
        </div>

        {/* Important Tip */}
        <div className="mb-8 p-6 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <p className="text-[var(--text-pri)] text-lg font-medium">
            "Things people don't know about you - strategic disclosure"
          </p>
          <p className="text-[var(--text-sec)] mt-2">
            Effective communication requires knowing what to share and what to keep private, 
            while also being open to feedback about what others see that you don't.
          </p>
        </div>

        {/* Main Johari Window */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">The Four Quadrants</h2>
          
          <div className="grid grid-cols-2 gap-4 max-w-4xl mx-auto">
            {quadrants.map((quadrant) => (
              <div key={quadrant.id} className={`p-6  border-2 ${getColorClasses(quadrant.color)}`}>
                <h3 className="text-xl font-bold mb-2">{quadrant.title}</h3>
                <p className="text-sm opacity-80 mb-4">{quadrant.description}</p>
                <div>
                  <h4 className="font-semibold mb-2">Examples:</h4>
                  <ul className="text-sm space-y-1">
                    {quadrant.examples.map((example, index) => (
                      <li key={index}>• {example}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Questions */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Key Questions to Ask</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-charcoal-800 mb-3">For Each Quadrant:</h3>
              <ul className="space-y-2 text-charcoal-600">
                <li>• <strong>Open:</strong> "What do we both know about me?"</li>
                <li>• <strong>Blind Spot:</strong> "What do others see that I don't?"</li>
                <li>• <strong>Hidden:</strong> "What do I know that others don't?"</li>
                <li>• <strong>Unknown:</strong> "What might we discover together?"</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-charcoal-800 mb-3">Strategic Questions:</h3>
              <ul className="space-y-2 text-charcoal-600">
                <li>• "What should I share to build trust?"</li>
                <li>• "What feedback do I need to seek?"</li>
                <li>• "What am I keeping hidden unnecessarily?"</li>
                <li>• "What could we explore together?"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Usage Examples */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">When to Use This Tool</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Team Building</h3>
              <p className="text-[var(--text-sec)] text-sm">Help team members understand each other better and build trust through appropriate disclosure.</p>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Self-Awareness</h3>
              <p className="text-[var(--text-sec)] text-sm">Increase your understanding of how others perceive you and what you might be missing.</p>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Communication</h3>
              <p className="text-[var(--text-sec)] text-sm">Improve communication by understanding what information to share and when.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
